/**
 * sameFieldValue — the ONE "is this field value unchanged?" comparison
 * (PN-STALE B2 spec v1.3 §2.2). reconcileConfirmations recomputes a carried
 * key's dirty flag with it. Never inline a second copy: a copy would look
 * canonical while this one drifted.
 *
 *  - undefined and null compare EQUAL. A key absent from a Load baseline and
 *    the null an edit-then-clear commits are the same "no value": on prod, 25
 *    of 32 editable keys arrive absent on some rows, and 14 arrive absent on
 *    some rows and stored null on others (Phase 2 audit pre-flight).
 *  - '' is DISTINCT from null. This is deliberately NOT normalizeForCompare's
 *    rule: that one collapses '' (and whitespace-only text) to null for the
 *    editors' did-anything-change checks. Here, a server-stored '' cleared to
 *    null is a real change and must stay dirty.
 *  - Everything else: strict equality for primitives; structural equality for
 *    objects and arrays — recursive, key-order independent, array order
 *    significant.
 *
 * PURE. No React, no Dexie.
 */

/**
 * Structural equality. Moved here from rebaseRecord.js's console probe so the
 * field zone holds one deep-equal, not two; that probe now imports it. One
 * guard added in the move: an array never equals a plain object, even one
 * with the same index keys.
 */
export function deepEqual(a, b) {
  if (a === b) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const ak = Object.keys(a);
  const bk = Object.keys(b);
  if (ak.length !== bk.length) return false;
  return ak.every((k) => Object.prototype.hasOwnProperty.call(b, k) && deepEqual(a[k], b[k]));
}

export function sameFieldValue(a, b) {
  if (a == null && b == null) return true; // undefined ≡ null — and nothing else (`== null` excludes '')
  return deepEqual(a, b);
}

export default sameFieldValue;
